import * as Sentry from "@sentry/react";

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const release = import.meta.env.VITE_SENTRY_RELEASE as string | undefined;
const isProd = import.meta.env.PROD;

/**
 * Initialize Sentry for the frontend.
 *
 * Errors-only: no tracing, no replay, no profiling. Fails open if the DSN is
 * missing (e.g. local dev, preview builds), so Sentry never blocks startup.
 */
export function initSentry(): void {
  Sentry.init({
    dsn,
    enabled: isProd && !!dsn,
    environment: "production",
    release,
    sendDefaultPii: false,
    sampleRate: 1.0,
    beforeSend(event, hint) {
      const err = hint?.originalException as
        | { name?: string; message?: string }
        | undefined;

      // Noise we never want paged on.
      if (err?.name === "AbortError") return null;
      if (err?.name === "CanceledError") return null;
      if (err?.message?.includes("ResizeObserver loop")) return null;

      // Browser extension injected scripts — not our code.
      const firstFrame = event.exception?.values?.[0]?.stacktrace?.frames?.[0];
      const filename = firstFrame?.filename ?? "";
      if (
        filename.startsWith("chrome-extension://") ||
        filename.startsWith("moz-extension://") ||
        filename.startsWith("safari-extension://")
      ) {
        return null;
      }

      scrubSecrets(event);
      return event;
    },
  });

  if (isProd && !dsn) {
    // eslint-disable-next-line no-console
    console.warn("[sentry] VITE_SENTRY_DSN not set — error reporting disabled");
  }
}

/**
 * Walk the event payload and replace values under keys that look like secrets
 * with "[filtered]". Defensive: Sentry's own scrubber handles common PII, but
 * we may attach request/response bodies via breadcrumbs that contain tokens.
 */
function scrubSecrets(event: Sentry.ErrorEvent): void {
  const SECRET_KEYS = /^(password|token|apikey|authorization|secret)$/i;

  const walk = (obj: unknown): void => {
    if (!obj || typeof obj !== "object") return;
    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      if (SECRET_KEYS.test(key)) {
        (obj as Record<string, unknown>)[key] = "[filtered]";
      } else if (value && typeof value === "object") {
        walk(value);
      }
    }
  };

  walk(event.extra);
  walk(event.contexts);
  walk(event.request?.headers);
  walk(event.request?.data);
}
